import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell } from './ui/table';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/toast';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Car, Package, Download, Clock, History } from 'lucide-react';
import api from '../services/api';
import dayjs from 'dayjs';

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-zinc-500">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
        </div>
        <Icon className={`h-8 w-8 ${color} opacity-50`} />
      </div>
    </CardContent>
  </Card>
);

const DeerReport = () => {
  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchReport = useCallback(async (sd, ed) => {
    setLoading(true);
    try {
      const res = await api.get('/reports/daily', { params: { startDate: sd, endDate: ed } });
      setData(res.data);
    } catch {
      toast({ title: 'Тайлан татахад алдаа гарлаа', variant: 'error' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReport(startDate, endDate); }, [startDate, endDate, fetchReport]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Тайлан</h2>
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={() => {
            const url = `${api.defaults.baseURL}/reports/export-excel?startDate=${startDate}&endDate=${endDate}`;
            const token = localStorage.getItem('token');
            fetch(url, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob())
              .then(blob => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `taillan_${startDate}_${endDate}.xlsx`;
                a.click();
              });
          }}>
            <Download className="h-4 w-4 mr-2" />Excel
          </Button>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" />
          <span className="text-sm text-zinc-500">—</span>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <StatCard title="Нийт" value={data.summary.total} icon={Package} color="text-zinc-700" />
            <StatCard title="Өнөөдрийн" value={data.summary.todayNew || 0} icon={Clock} color="text-indigo-600" />
            <StatCard title="Өмнөх өдрөөс" value={data.summary.fromPrevious || 0} icon={History} color="text-amber-600" />
            <StatCard title="Хүргэсэн" value={data.summary.delivered} icon={CheckCircle} color="text-green-600" />
            <StatCard title="Цуцалсан" value={data.summary.failed} icon={XCircle} color="text-red-600" />
            <StatCard title="Хүргэлтэнд" value={data.summary.total - data.summary.delivered - data.summary.failed} icon={Car} color="text-blue-600" />
          </div>

          {Object.keys(data.failReasons).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Цуцалсан шалтгаанууд</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.failReasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                    <Badge key={reason} variant="destructive" className="text-sm px-3 py-1">
                      {reason}: {count}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Хүргэлтийн ажилтан бүрээр</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Хүргэлтийн ажилтан</TableHead>
                      <TableHead>Утас</TableHead>
                      <TableHead>Нийт</TableHead>
                      <TableHead>Өнөөдрийн</TableHead>
                      <TableHead>Өмнөхөөс</TableHead>
                      <TableHead>Хүргэсэн</TableHead>
                      <TableHead>Цуцалсан</TableHead>
                      <TableHead>Хүргэлтэнд</TableHead>
                      <TableHead>Шалтгаан</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.byDriver.map(r => (
                      <TableRow key={r.driverId}>
                        <TableCell className="font-medium">{r.driverName}</TableCell>
                        <TableCell>{r.driverPhone}</TableCell>
                        <TableCell>{r.total}</TableCell>
                        <TableCell><Badge variant="info">{r.todayNew || 0}</Badge></TableCell>
                        <TableCell><Badge variant="warning">{r.fromPrevious || 0}</Badge></TableCell>
                        <TableCell><Badge variant="success">{r.delivered}</Badge></TableCell>
                        <TableCell>{r.failed > 0 ? <Badge variant="destructive">{r.failed}</Badge> : '0'}</TableCell>
                        <TableCell>{(r.total - r.delivered - r.failed) > 0 ? <Badge variant="info">{r.total - r.delivered - r.failed}</Badge> : '0'}</TableCell>
                        <TableCell>
                          {Object.entries(r.failReasons || {}).length > 0
                            ? Object.entries(r.failReasons).map(([reason, count]) => (
                                <Badge key={reason} variant="destructive" className="mr-1 mb-1 text-xs">{reason}: {count}</Badge>
                              ))
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {data.byDriver.length > 0 && (
                    <TableFooter>
                      <TableRow className="font-bold">
                        <TableCell>Нийт</TableCell>
                        <TableCell />
                        <TableCell>{data.byDriver.reduce((s, r) => s + r.total, 0)}</TableCell>
                        <TableCell><Badge variant="info">{data.byDriver.reduce((s, r) => s + (r.todayNew || 0), 0)}</Badge></TableCell>
                        <TableCell><Badge variant="warning">{data.byDriver.reduce((s, r) => s + (r.fromPrevious || 0), 0)}</Badge></TableCell>
                        <TableCell><Badge variant="success">{data.byDriver.reduce((s, r) => s + r.delivered, 0)}</Badge></TableCell>
                        <TableCell><Badge variant="destructive">{data.byDriver.reduce((s, r) => s + r.failed, 0)}</Badge></TableCell>
                        <TableCell><Badge variant="info">{data.byDriver.reduce((s, r) => s + r.total - r.delivered - r.failed, 0)}</Badge></TableCell>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};

export default DeerReport;
